import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DropdownItem, ModalService } from '@vinnova/ui';
import { Worksite } from '../../../../core/models/question/question-worksite.model';
import { Stodgrund } from '../../../../core/models/stodgrund.model';
import { BudgetService } from '../../../../core/services/budget.service';

@UntilDestroy()
@Component({
  selector: 'lib-masshantera-stodgrund',
  templateUrl: './masshantera-stodgrund.component.html',
  styleUrl: './masshantera-stodgrund.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MasshanteraStodgrundComponent implements OnInit {
  worksites: Worksite[];
  injector = inject(Injector);
  selectedWorksites;
  allWorksitesSelected: boolean;
  changedStodgrund = output<{
    worksites: string[];
    stodgrundId: string;
    sfsStodgrundId: string;
  }>();

  stodgrund: string;
  sfsStodgrund: string;

  stodgrunder: Stodgrund[];
  stodgrunderItems: DropdownItem[];
  sfsStodgrunderItems;

  validated = signal(false);

  get anyWorksiteSelected() {
    for (const worksiteId of Object.keys(this.selectedWorksites)) {
      if (this.selectedWorksites[worksiteId]) {
        return true;
      }
    }
    return false;
  }

  get hasValidationError() {
    return (
      !this.anyWorksiteSelected ||
      !this.stodgrund ||
      (this.sfsStodgrunderItems[this.stodgrund]?.length > 0 &&
        !this.sfsStodgrund)
    );
  }

  constructor(
    public modalService: ModalService,
    private budgetService: BudgetService,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit(): void {
    this.selectedWorksites = {};
    for (const worksite of this.worksites) {
      this.selectedWorksites[worksite.id] = false;
    }

    this.stodgrunder = this.budgetService.stodgrunder;

    this.translocoService.langChanges$
      .pipe(untilDestroyed(this))
      .subscribe((lang) => {
        this.setStodgrundItems(lang);
        this.setSfsStodgrundItems(lang);
      });
  }

  updateStodgrund() {
    this.validated.set(true);
    if (this.hasValidationError) {
      return;
    }

    const worksites = [];
    for (const worksiteId of Object.keys(this.selectedWorksites)) {
      if (this.selectedWorksites[worksiteId]) {
        worksites.push(worksiteId);
      }
    }

    this.changedStodgrund.emit({
      worksites: worksites,
      stodgrundId: this.stodgrund,
      sfsStodgrundId: this.sfsStodgrund,
    });
    this.modalService.hide();
  }

  toggleSelectAll(event) {
    for (const worksite of this.worksites) {
      this.selectedWorksites[worksite.id] = event;
    }
  }

  selectedWorksiteChange() {
    let isAllSelected = true;
    for (const worksite of this.worksites) {
      const selected = this.selectedWorksites[worksite.id];
      if (!selected) {
        isAllSelected = false;
        break;
      }
    }

    let isAllDeselected = true;
    for (const worksite of this.worksites) {
      const selected = this.selectedWorksites[worksite.id];
      if (selected) {
        isAllDeselected = false;
        break;
      }
    }

    if (!this.allWorksitesSelected && isAllSelected) {
      this.allWorksitesSelected = true;
    } else if (this.allWorksitesSelected && isAllDeselected) {
      this.allWorksitesSelected = false;
    }
  }

  private setStodgrundItems(lang) {
    this.stodgrunderItems = this.stodgrunder
      .filter((s) => s.aktiv)
      .map((s) => {
        return {
          label: lang === 'sv' ? s.stodgrundName : s.stodgrundNameEng,
          value: s.stodgrundId.toString(),
        };
      });
  }

  private setSfsStodgrundItems(lang: string) {
    this.sfsStodgrunderItems = {};
    for (const stodgrund of this.stodgrunder.filter((s) => s.aktiv)) {
      this.sfsStodgrunderItems[stodgrund.stodgrundId.toString()] =
        stodgrund.paragrafer.map((p) => {
          return {
            label: lang === 'sv' ? p.stodgrundName : p.stodgrundNameEng,
            value: p.stodgrundId.toString(),
          };
        });
    }
  }
}
